import { useState } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { ProductFiltersModal } from './ProductFiltersModal'
import { usePOSStore } from '@/stores/posStore'
import { useBrands, useCategories, useTypes, useUnits, type ProductType, type ProductUnit } from '@/api/products'
import type { UIProductFilters, StrainGroup } from '@/types'

// Same display order + classification rules as the modal. Kept as a
// local copy so the bar's chip set matches whatever the modal renders
// without importing through the modal file (which would pull React
// state and effects into this lightweight summary row).
const STRAIN_GROUPS: StrainGroup[] = ['Indica', 'Sativa', 'Hybrid', 'Blend']

function classifyStrainType(name: string): StrainGroup | null {
  const n = name.trim()
  if (n.toLowerCase() === 'hybrid') return 'Hybrid'
  if (/indica/i.test(n)) return 'Indica'
  if (/sativa/i.test(n)) return 'Sativa'
  if (/blend/i.test(n)) return 'Blend'
  return null
}

function groupStrainTypes(types: ProductType[]): Record<StrainGroup, number[]> {
  const groups: Record<StrainGroup, number[]> = { Indica: [], Sativa: [], Hybrid: [], Blend: [] }
  for (const t of types) {
    const g = classifyStrainType(t.name)
    if (g) groups[g].push(t.id)
  }
  return groups
}

// Cap on chips rendered next to the Filters button — anything past
// this is reachable via the modal so the bar doesn't overflow on
// narrow screens.
const MAX_VISIBLE_CHIPS = 4

interface ActiveFilterChip {
  key: string
  label: string
  onRemove: () => void
}

export function ProductFiltersBar() {
  const { searchQuery, setSearchQuery, uiFilters, setUIFilters, clearUIFilters } = usePOSStore()
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)

  const { data: brands = [] } = useBrands()
  const { data: categories = [] } = useCategories()
  const { data: productTypes = [] } = useTypes()
  const { data: productUnits = [] } = useUnits()

  const chips = buildActiveFilterChips(uiFilters, setUIFilters, brands, categories, productTypes, productUnits)
  const activeFilterCount = chips.length
  const visibleChips = chips.slice(0, MAX_VISIBLE_CHIPS)
  const hiddenCount = chips.length - visibleChips.length

  const handleFiltersChange = (newFilters: UIProductFilters) => {
    setUIFilters(newFilters)
  }

  return (
    <div className="bg-white border-b border-gray-200 py-2 md:py-3 px-3 md:px-4 flex-shrink-0">
      <div className="flex items-center gap-3">
        <Search size={18} className="text-gray-400 flex-shrink-0" />
        <input
          type="text"
          placeholder="Search products by name or brand..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 min-w-[140px] py-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-400"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>
        )}

        {visibleChips.length > 0 && (
          <div className="hidden md:flex items-center gap-1.5 flex-shrink min-w-0 overflow-hidden">
            {visibleChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200 max-w-[160px]"
              >
                <span className="truncate">{chip.label}</span>
                <button
                  type="button"
                  onClick={chip.onRemove}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-green-100 text-green-600 hover:text-green-800 cursor-pointer flex-shrink-0"
                  title={`Remove ${chip.label}`}
                  aria-label={`Remove ${chip.label}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setIsFiltersModalOpen(true)}
                className="inline-flex items-center px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-full cursor-pointer"
              >
                +{hiddenCount} more
              </button>
            )}
          </div>
        )}

        <button
          onClick={() => setIsFiltersModalOpen(true)}
          className={`relative flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer flex-shrink-0 ${
            activeFilterCount > 0
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <SlidersHorizontal size={16} />
          <span className="hidden md:inline">Filters</span>
          {activeFilterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        {activeFilterCount > 0 && (
          <button
            onClick={clearUIFilters}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            title="Clear all filters"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {isFiltersModalOpen && (
        <ProductFiltersModal
          filters={uiFilters}
          onFiltersChange={handleFiltersChange}
          onClose={() => setIsFiltersModalOpen(false)}
          brands={brands}
        />
      )}
    </div>
  )
}

// Flatten active filters into removable chips. Order matters — the
// list is sliced at MAX_VISIBLE_CHIPS, so the most useful filters
// (categories, brands) come first and presets last.
function buildActiveFilterChips(
  filters: UIProductFilters,
  setFilters: (next: UIProductFilters) => void,
  brands: { id: number; name: string }[],
  categories: { id: number; name: string }[],
  productTypes: ProductType[],
  productUnits: ProductUnit[],
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []

  for (const id of filters.categoryGroups) {
    const name = categories.find((c) => c.id === id)?.name ?? `Category ${id}`
    chips.push({
      key: `cat-${id}`,
      label: name,
      onRemove: () =>
        setFilters({
          ...filters,
          categoryGroups: filters.categoryGroups.filter((x) => x !== id),
        }),
    })
  }

  for (const id of filters.brands) {
    const name = brands.find((b) => b.id === id)?.name ?? `Brand ${id}`
    chips.push({
      key: `brand-${id}`,
      label: name,
      onRemove: () =>
        setFilters({
          ...filters,
          brands: filters.brands.filter((x) => x !== id),
        }),
    })
  }

  for (const w of filters.weights) {
    chips.push({
      key: `weight-${w}`,
      label: w,
      onRemove: () =>
        setFilters({
          ...filters,
          weights: filters.weights.filter((x) => x !== w),
        }),
    })
  }

  // Strain chips are grouped (Indica/Sativa/Hybrid/Blend) — show one
  // chip per active group rather than one per type ID, so removing
  // "Indica" drops both Indica + Indica Dominant IDs in a single click.
  // Matches the modal's selection model.
  const strainGroupIds = groupStrainTypes(productTypes)
  for (const group of STRAIN_GROUPS) {
    const groupSet = new Set(strainGroupIds[group])
    if (groupSet.size === 0) continue
    if (!filters.productTypes.some((id) => groupSet.has(id))) continue
    chips.push({
      key: `strain-${group}`,
      label: group,
      onRemove: () =>
        setFilters({
          ...filters,
          productTypes: filters.productTypes.filter((id) => !groupSet.has(id)),
        }),
    })
  }

  for (const id of filters.units) {
    const name = productUnits.find((u) => u.id === id)?.name ?? `Unit ${id}`
    chips.push({
      key: `unit-${id}`,
      label: name,
      onRemove: () =>
        setFilters({
          ...filters,
          units: filters.units.filter((x) => x !== id),
        }),
    })
  }

  const PRICE_LABELS: Record<string, string> = {
    under20: 'Under $20',
    '20to40': '$20 – $40',
    '40to80': '$40 – $80',
    over80: '$80+',
  }
  for (const p of filters.priceRanges) {
    chips.push({
      key: `price-${p}`,
      label: PRICE_LABELS[p] ?? p,
      onRemove: () =>
        setFilters({
          ...filters,
          priceRanges: filters.priceRanges.filter((x) => x !== p),
        }),
    })
  }

  if (filters.thcRange[0] > 0 || filters.thcRange[1] < 100) {
    chips.push({
      key: 'thc',
      label: `THC ${filters.thcRange[0]}–${filters.thcRange[1]}%`,
      onRemove: () => setFilters({ ...filters, thcRange: [0, 100] }),
    })
  }
  if (filters.cbdRange[0] > 0 || filters.cbdRange[1] < 100) {
    chips.push({
      key: 'cbd',
      label: `CBD ${filters.cbdRange[0]}–${filters.cbdRange[1]}%`,
      onRemove: () => setFilters({ ...filters, cbdRange: [0, 100] }),
    })
  }

  return chips
}
